import { find, flatMap, compact, flow } from 'lodash'
import * as jsonpatch from 'jsonpatch'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import { FieldType, PermissionRole } from '@wix/wix-data-schema-types'
import type {
  CollectionsApi,
  Transport,
  SchemaOperation,
  GetAllOptions,
} from './types'
import { SchemaOperationType } from './types'
import normalizeFieldIndices from './normalizeFieldIndices'
import { ensurePermissions } from './permissions'
import sortFieldsByIndicesAndRemoveIndices from './sortFieldsByIndicesAndRemoveIndices'

const isValidSchema = (schema: Schema) => schema && schema.fields

const createInitialSchema = (
  collectionId: string,
  ownerAppId?: string,
  version?: number,
): Schema => ({
  id: collectionId,
  displayName: collectionId,
  displayField: 'title',
  fields: {
    title: {
      displayName: 'Title',
      type: FieldType.text,
      systemField: false,
    },
  },
  permissions: {
    read: PermissionRole.admin,
    update: PermissionRole.admin,
    insert: PermissionRole.admin,
    remove: PermissionRole.admin,
  },
  plugins: {},
  ownerAppId,
  version,
})

const postProcessSchema = flow([
  normalizeFieldIndices,
  sortFieldsByIndicesAndRemoveIndices,
  ensurePermissions,
])

const publishingPluginDisabled = (schema: Schema): boolean =>
  !schema.plugins?.publishing
const removePublishingFields = (schema: Schema): Schema => {
  const { _publishStatus, _publishDate, _draftDate, ...remainingFields } =
    schema.fields
  return { ...schema, fields: remainingFields }
}

const applyImplicitFixesOnUpdatedSchema = (schema: Schema): Schema =>
  publishingPluginDisabled(schema) ? removePublishingFields(schema) : schema

const createCollectionsApi = ({
  transportLayer: { loadSchema, listSchemas, saveSchema, removeSchema },
  ownerAppId,
}: {
  transportLayer: Transport
  ownerAppId?: string
}): CollectionsApi => {
  const fetchSchema = async (collectionId: string) => {
    const schema = await loadSchema(collectionId)
    if (!isValidSchema(schema)) {
      throw new Error('Invalid schema structure')
    }
    return schema
  }

  const execute = async (operation: SchemaOperation) => {
    const { collectionId, type } = operation
    const patches = flatMap(operation.patches)

    switch (type) {
      case SchemaOperationType.CREATE: {
        const schema = await listSchemas({
          includeDeletedCollections: true,
        }).then(schemas => schemas.find(schema => schema.id === collectionId))
        const version = schema?.isDeleted ? schema.version : undefined
        const createdSchema = normalizeFieldIndices(
          jsonpatch.apply_patch(
            createInitialSchema(collectionId, ownerAppId, version),
            patches,
          ),
        )
        return saveSchema(collectionId, createdSchema)
      }
      case SchemaOperationType.REMOVE:
        return removeSchema(collectionId)

      case SchemaOperationType.RESTORE: {
        const restoredSchema = normalizeFieldIndices(
          jsonpatch.apply_patch(createInitialSchema(collectionId), [
            ...patches,
            { op: 'add', path: '/isDeleted', value: false },
          ]),
        )
        return saveSchema(collectionId, restoredSchema)
      }
      case SchemaOperationType.UPDATE: {
        const updatedSchema = normalizeFieldIndices(
          jsonpatch.apply_patch(await fetchSchema(collectionId), patches),
        )
        return saveSchema(
          collectionId,
          applyImplicitFixesOnUpdatedSchema(updatedSchema),
        )
      }
      default:
        throw new Error(`Unknown schema operation: ${type}`)
    }
  }

  const get = async (collectionId: string) => {
    const schema = await loadSchema(collectionId)
    return isValidSchema(schema)
      ? postProcessSchema(schema)
      : Promise.reject(new Error('Invalid schema structure'))
  }
  const getSome = async (collectionIds: string[]) => {
    return (await listSchemas())
      .filter(isValidSchema)
      .filter(({ id }) => collectionIds.includes(id))
      .map(postProcessSchema)
  }
  const getAll = async (options?: GetAllOptions) => {
    return (await listSchemas(options))
      .filter(isValidSchema)
      .map(postProcessSchema)
  }
  const getReferring = async (collectionId: string) => {
    const schemas = await listSchemas()
    const referringSchemas = schemas
      .filter(schema => collectionId !== schema.id)
      .filter(schema =>
        find(
          schema.fields,
          (field: FieldSpec) =>
            'referencedCollection' in field &&
            field.referencedCollection === collectionId,
        ),
      )
    return compact(referringSchemas).map(postProcessSchema)
  }

  return {
    execute,
    get,
    getSome,
    getAll,
    getReferring,
  }
}

export default createCollectionsApi
